import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/theme.css";
import "./Checkout.css";            // keep your page-specific bits
import "../pages/Login.css";        // reuse the login card styles
import Footer from "../components/Footer";
import BankIDLogin from "../components/BankIDLogin";
import PasswordInput from "../components/PasswordInput"; // ⬅️ same component as Login

import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  signOut,
  signInWithEmailAndPassword,
} from "firebase/auth";

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    selectedPlan,
    selectedAddOns = [],
    totalPrice = 0,
    selectedPayment,
    billingCycle,
    isOneTime = false,
  } = location.state || {};

  // ── auth UI state (same as Login)
  const [manualSignup, setManualSignup] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [sending, setSending] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [user, setUser] = useState(null);

  // banner shown after we send the password-setup link (stay on Checkout)
  const [justCreated, setJustCreated] = useState(false);
  const [createdEmail, setCreatedEmail] = useState("");

  const [userDetails, setUserDetails] = useState({ name: "", email: "" });
  const [emailLogin, setEmailLogin] = useState({ email: "", password: "" });

  // If plan is missing, go back to pricing
  useEffect(() => {
    if (!selectedPlan) navigate("/pricing");
  }, [selectedPlan, navigate]);

  // Watch auth state (if logged in, hide auth card)
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (fbUser) => {
      setUser(fbUser || null);
    });
  }, []);

  // ── Sign in with Google (same flow as Login)
  const handleGoogleLogin = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      await signInWithPopup(auth, provider);
      // stay on checkout; user will see summary and Pay button
    } catch (err) {
      console.error("Google Login Failed:", err);
      alert(t("login.error_google") || "Google sign-in failed. Please try again.");
    }
  };

  // ── Manual signup: Full name + Email only (send reset link, open email login)
  const handleManualSignup = async () => {
    const name = (userDetails.name || "").trim();
    const email = (userDetails.email || "").trim();

    if (!name || !email) {
      alert(t("login.fill_all_fields") || "Please fill in all fields.");
      return;
    }

    const auth = getAuth();
    const tempPassword = Math.random().toString(36).slice(-10) + "A1!";

    try {
      setSending(true);

      // Create user (auto signs in temporarily)
      const cred = await createUserWithEmailAndPassword(auth, email, tempPassword);
      if (name) await updateProfile(cred.user, { displayName: name });

      // Send password setup link
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/login`, // where they’ll land if they open mail later
        handleCodeInApp: false,
      });

      // sign out so header shows logged-out state until they sign in
      await signOut(auth);

      // Open the email login form here, prefill email + show banner
      setManualSignup(false);
      setShowEmailLogin(true);
      setJustCreated(true);
      setCreatedEmail(email);
      setUserDetails({ name: "", email: "" });
      setEmailLogin((s) => ({ ...s, email }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err?.code === "auth/email-already-in-use") {
        try {
          await sendPasswordResetEmail(auth, email, {
            url: `${window.location.origin}/login`,
            handleCodeInApp: false,
          });
          await signOut(auth);
          setManualSignup(false);
          setShowEmailLogin(true);
          setJustCreated(true);
          setCreatedEmail(email);
          setUserDetails({ name: "", email: "" });
          setEmailLogin((s) => ({ ...s, email }));
        } catch (resetErr) {
          console.error("Send reset error:", resetErr);
          alert(t("login.error_reset_send") || "Could not send the email. Please try again.");
        }
      } else {
        console.error("Manual signup error:", err);
        alert(t("login.error_manual_signup") || "Could not create the account. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  // ── Email + password sign-in (same as Login)
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    const email = emailLogin.email.trim();
    const password = emailLogin.password;

    if (!email || !password) {
      alert(t("login.fill_all_fields") || "Please fill in all fields.");
      return;
    }

    setSigningIn(true);
    try {
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email, password);
      setJustCreated(false);
    } catch (err) {
      console.error("Email sign-in error:", err);
      alert(
        t("login.error_email_signin") ||
          "We couldn't sign you in with that email/password. Please try again."
      );
    } finally {
      setSigningIn(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!emailLogin.email) {
      alert(t("login.enter_email_first") || "Please enter your email first.");
      return;
    }
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, emailLogin.email, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });
      alert(
        t("login.reset_sent_banner", { email: emailLogin.email }) ||
          `We sent a reset link to ${emailLogin.email}`
      );
    } catch (err) {
      console.error("Forgot password error:", err);
      alert(t("login.error_reset_send") || "Could not send the email. Please try again.");
    }
  };

  // ── Proceed to payment
  const handleProceedToPayment = () => {
    if (!user) {
      alert(t("checkout.login_required"));
      return;
    }

    navigate("/payment", {
      state: {
        selectedPlan,
        selectedAddOns,
        totalPrice,
        selectedPayment,
        billingCycle,
        isOneTime,
        userDetails: { name: user.displayName || "", email: user.email || "" },
      },
    });
  };

  // Payment method label
  const paymentLabelMap = {
    stripe: t("pricing.methods.card"),
    paypal: t("pricing.methods.paypal"),
    bank: t("pricing.methods.bank"),
  };
  const paymentLabel = selectedPayment ? paymentLabelMap[selectedPayment] : "";

  return (
    <div className="checkout-page-wrapper">
      <div className="login-container"> {/* reuse the same card shell */}
        <h1 className="checkout-title">{t("checkout.title")}</h1>

        {/* AUTH CARD — replicated from Login page */}
        {!user && (
          <>
            <h2 style={{ marginTop: 6 }}>
              {t("checkout.sign_in_to_continue") || t("login.title")}
            </h2>

            {justCreated && (
              <div className="notice success" style={{ marginBottom: 12 }}>
                {t("login.reset_sent_banner", { email: createdEmail })}
              </div>
            )}

            {/* OAuth buttons */}
            <button className="btn btn-google" type="button" onClick={handleGoogleLogin}>
              {t("login.google_sign_in")}
            </button>

            <BankIDLogin onSuccess={() => {}} className="btn btn-bankid" />

            {/* Toggles */}
            <div className="login-toggles">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setManualSignup((v) => !v);
                  if (showEmailLogin) setShowEmailLogin(false);
                }}
              >
                {t("login.manual_signup")}
              </button>

              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setShowEmailLogin((v) => !v);
                  if (manualSignup) setManualSignup(false);
                }}
              >
                {t("login.email_login_toggle") || "Sign in with Email"}
              </button>
            </div>

            {/* Manual signup form — Full name + Email only */}
            {manualSignup && (
              <div className="manual-signup-form card-section">
                <input
                  type="text"
                  placeholder={t("login.name")}
                  value={userDetails.name}
                  onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
                  required
                  className="ui-input"
                />
                <input
                  type="email"
                  placeholder={t("login.email")}
                  value={userDetails.email}
                  onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                  required
                  className="ui-input"
                />

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleManualSignup}
                  disabled={sending}
                >
                  {sending ? t("login.sending_reset") : t("login.create_account")}
                </button>

                <p className="hint">{t("login.create_flow_hint")}</p>
              </div>
            )}

            {/* Email + Password login form */}
            {showEmailLogin && (
              <form className="email-login-form card-section" onSubmit={handleEmailSignIn}>
                <input
                  type="email"
                  placeholder={t("login.email")}
                  value={emailLogin.email}
                  onChange={(e) => setEmailLogin({ ...emailLogin, email: e.target.value })}
                  required
                  className="ui-input"
                  autoComplete="username"
                />

                <PasswordInput
                  value={emailLogin.password}
                  onChange={(e) => setEmailLogin({ ...emailLogin, password: e.target.value })}
                  placeholder={t("login.password") || "Password"}
                  required
                  name="password"
                  autoComplete="current-password"
                />

                <button type="submit" className="btn btn-primary" disabled={signingIn}>
                  {signingIn ? t("login.signing_in") || "Signing in…" : t("login.sign_in") || "Sign in"}
                </button>

                <button type="button" className="btn btn-link" onClick={handleForgotPassword}>
                  {t("login.forgot_password") || "Forgot password?"}
                </button>
              </form>
            )}
          </>
        )}

        {/* CHECKOUT SUMMARY — visible once logged in */}
        {user && (
          <>
            <p className="logged-in-user">
              {t("checkout.logged_in_as")}: {user.displayName || user.email} ({user.email})
            </p>

            <div className="checkout-summary">
              <h2>
                {t("checkout.selected_plan")}: {selectedPlan}
              </h2>

              {selectedAddOns.length > 0 ? (
                <ul>
                  {selectedAddOns.map((addon, i) => (
                    <li key={i}>
                      {addon.name} - ${addon.price}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t("checkout.no_addons")}</p>
              )}

              <h3>
                {t("checkout.total")}: ${totalPrice}
              </h3>
            </div>

            <div className="checkout-payment">
              <h2>{t("checkout.payment_method")}</h2>
              <p>{paymentLabel}</p>
            </div>

            <button className="checkout-btn" onClick={handleProceedToPayment}>
              {t("checkout.pay_now")}
            </button>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;